import { Link } from "react-router-dom";
import { Shield, Phone, Mail, MapPin, Instagram, Facebook, Linkedin, Youtube } from "lucide-react";

const productLinks = [
  { to: "/services/health", label: "Health Insurance" },
  { to: "/services/life", label: "Life Insurance" },
  { to: "/services/term", label: "Term Insurance" },
  { to: "/services/motor", label: "Motor Insurance" },
  { to: "/services/mutual-funds", label: "Mutual Funds" },
];
const quickLinks = [
  { to: "/about", label: "About Us" },
  { to: "/services", label: "All Services" },
  { to: "/calculators", label: "Calculators" },
  { to: "/knowledge", label: "Knowledge Centre" },
  { to: "/assessment", label: "Needs Assessment" },
  { to: "/contact", label: "Contact" },
];

export default function Footer() {
  return (
    <footer data-testid="site-footer" className="bg-slate-950 text-slate-300 mt-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <Link to="/" className="inline-flex items-center gap-3">
              <span className="inline-flex items-center justify-center rounded-xl bg-white p-2">
                <img src="/samota-logo.png" alt="Samota Insurance" className="h-10 w-auto" />
              </span>
              <span className="flex flex-col leading-tight">
                <span className="text-[11px] text-emerald-300 italic tracking-wide">We insure your happiness</span>
              </span>
            </Link>
            <p className="mt-6 text-sm leading-relaxed text-slate-400 max-w-sm">
              We insure your happiness. A family-run insurance and financial planning firm since 1987,
              now led by Vinay Samota, CFP® — bringing 20+ years of personal advisory expertise to
              every family and business we serve.
            </p>
            <div className="mt-6 flex items-center gap-3">
              {[
                { Icon: Instagram, label: "Instagram", href: "https://www.instagram.com/samotainsurance" },
                { Icon: Linkedin, label: "LinkedIn", href: "https://www.linkedin.com/in/vinaysamotacfp" },
                { Icon: Facebook, label: "Facebook", href: "https://www.facebook.com/samotainsurance/" },
                { Icon: Youtube, label: "YouTube", href: "https://www.youtube.com/@samotainsurance" },
              ].map(({ Icon, label, href }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={label}
                  data-testid={`footer-social-${label.toLowerCase()}`}
                  className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-white/5 text-slate-300 hover:bg-emerald-500 hover:text-white transition"
                >
                  <Icon className="h-4 w-4" />
                </a>
              ))}
            </div>
          </div>

          <div className="lg:col-span-2">
            <h4 className="font-display text-sm font-semibold text-white uppercase tracking-wider">
              Company
            </h4>
            <ul className="mt-5 space-y-3">
              {quickLinks.map((l) => (
                <li key={l.to}>
                  <Link
                    to={l.to}
                    className="text-sm text-slate-400 hover:text-white transition"
                    data-testid={`footer-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-3">
            <h4 className="font-display text-sm font-semibold text-white uppercase tracking-wider">
              Solutions
            </h4>
            <ul className="mt-5 space-y-3">
              {productLinks.map((l) => (
                <li key={l.to}>
                  <Link
                    to={l.to}
                    className="text-sm text-slate-400 hover:text-white transition"
                    data-testid={`footer-product-${l.to.split("/").pop()}`}
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-3">
            <h4 className="font-display text-sm font-semibold text-white uppercase tracking-wider">
              Reach us
            </h4>
            <ul className="mt-5 space-y-4 text-sm text-slate-400">
              <li className="flex gap-3">
                <MapPin className="h-4 w-4 mt-0.5 text-emerald-400 shrink-0" />
                <span>
                  Office No. 103, Sagar Heights,<br />
                  Near Jawahar Library, Station Road,<br />
                  Kalwa West, Thane – 400605
                </span>
              </li>
              <li className="flex gap-3">
                <Phone className="h-4 w-4 mt-0.5 text-emerald-400 shrink-0" />
                <a href="tel:+919821237924" className="hover:text-white" data-testid="footer-phone">
                  +91 98212 37924
                </a>
              </li>
              <li className="flex gap-3">
                <Mail className="h-4 w-4 mt-0.5 text-emerald-400 shrink-0" />
                <a href="mailto:office@samotainsurance.com" className="hover:text-white" data-testid="footer-email">
                  office@samotainsurance.com
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-white/10 text-xs text-slate-500 leading-relaxed">
          <p className="max-w-4xl">
            <strong className="text-slate-300">Disclaimer:</strong> Insurance is the subject matter of solicitation.
            Insurance is subject to policy terms and conditions. Claims are subject to insurer approval.
            Mutual Fund investments are subject to market risks. Please read all scheme-related documents
            carefully before investing. Samota Insurance n Investments acts as an authorised intermediary
            facilitating financial products.
          </p>
          <div className="mt-6 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <p>© {new Date().getFullYear()} Samota Insurance n Investments. All rights reserved.</p>
            <p>Founder: <span className="text-slate-300">Vinay Samota, CFP®</span></p>
          </div>
        </div>
      </div>
    </footer>
  );
}
